import React, { useState, useEffect, useRef } from 'react';
import Slider from 'react-slick';
import './AlwaysConnectedSlider.scss';
import leftArrow from '../../../../assets/smartxonnect/left.webp';
import rightArrow from '../../../../assets/smartxonnect/right.webp';
import whiteLeftArrow from '../../../../assets/smartxonnect/left.webp';
import whiteRightArrow from '../../../../assets/smartxonnect/right.webp';

/* ------------------ ARROWS ------------------ */
/* ------------------ ARROWS ------------------ */
const PrevArrow = ({ className, onClick, isDisabled }) => {
  const handlePrevClick = () => {
    if (!isDisabled) {
      dataLayer.push({
        event: 'always_connected_banner_navigation',
        action: 'previous_arrow_clicked',
        BrandName: 'TVS iQube',
        VariantName: 'SmartXonnect',
      });
      onClick();
    }
  };

  return (
    <div className={`${className} custom-prev-arrow`} onClick={handlePrevClick}>
      <img src={isDisabled ? whiteLeftArrow : leftArrow} alt="Previous" loading="lazy" />
    </div>
  );
};

const NextArrow = ({ className, onClick, isDisabled }) => {
  const handleNextClick = () => {
    if (!isDisabled) {
      dataLayer.push({
        event: 'always_connected_banner_navigation',
        action: 'next_arrow_clicked',
        BrandName: 'TVS iQube',
        VariantName: 'SmartXonnect',
      });
      onClick();
    }
  };

  return (
    <div className={`${className} custom-next-arrow`} onClick={handleNextClick}>
      <img
        src={isDisabled ? whiteLeftArrow : rightArrow}
        alt="Next"
        className={isDisabled ? 'disabled' : 'rotate-180'}
        loading="lazy"
      />
    </div>
  );
};

/* ------------------ MAIN COMPONENT ------------------ */
const AlwaysConnectedSlider = (props) => {
  const sliderRef = useRef(null);

  /* ---------- DESKTOP STATES ---------- */
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFirstSlide, setIsFirstSlide] = useState(true);
  const [isLastSlide, setIsLastSlide] = useState(false);
  const [isDataLayerTriggered, setIsDataLayerTriggered] = useState(false);

  /* ---------- MOBILE STATES ---------- */
  const [isMobile, setIsMobile] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(0);
  const [showAll, setShowAll] = useState(false);

  const slides = props?.fields?.SliderCards || [];
  const slidesToShow = 3;

  /* ------------------ MOBILE DETECTION ------------------ */
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 767);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /* ------------------ DESKTOP SLIDER SETTINGS ------------------ */
  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow,
    slidesToScroll: 3,
    prevArrow: <PrevArrow isDisabled={isFirstSlide} />,
    nextArrow: <NextArrow isDisabled={isLastSlide} />,
    afterChange: (current) => {
      setCurrentSlide(current);
      setIsFirstSlide(current === 0);
      setIsLastSlide(current + slidesToShow >= slides.length);
    },
  };

  /* ------------------ DATA LAYER (VIEW EVENT) ------------------ */
  useEffect(() => {
    if (!sliderRef.current || isDataLayerTriggered) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          dataLayer.push({
            event: 'always_connected_banner_navigation',
            BrandName: 'TVS iQube',
            VariantName: 'SmartXonnect',
          });
          setIsDataLayerTriggered(true);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(sliderRef.current);
    return () => observer.disconnect();
  }, [isDataLayerTriggered]);

  /* ------------------ MOBILE DATA ------------------ */
  const visibleMobileSlides = showAll ? slides : slides.slice(0, 3);

  /* ------------------ RENDER ------------------ */
  return (
    <div
      className="always-connected-slider-wrapper commonContainer"
      ref={sliderRef}
      data-aos="fade-up"
    >
      <div className="header-section-wrapper">
        <h2>
          <span>3</span> Always Connected
        </h2>
      </div>

      {/* ================= MOBILE ACCORDION ================= */}
      {isMobile ? (
        <div className="mobile-accordion">
          {visibleMobileSlides.map((item, index) => {
            const isExpanded = expandedIndex === index;
            const headerId = `accordion-header-${index}`;
            const panelId = `accordion-panel-${index}`;

            return (
              <div
                key={index}
                className={`accordion-item ${isExpanded ? 'expanded' : ''}`}
                id={`accordion-item-${index}`}
              >
                {/* ---------- HEADER ---------- */}
                <div
                  id={headerId}
                  className="accordion-header"
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  aria-controls={panelId}
                  onClick={() => setExpandedIndex(isExpanded ? null : index)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setExpandedIndex(isExpanded ? null : index);
                    }
                  }}
                >
                  <span className="serial-number">{String(index + 1).padStart(2, '')}</span>

                  <span className="header-text">
                    <span className="header-bar">|</span>{' '}
                    <span className="main-header-text">{item?.fields?.CardTitle?.value}</span>
                  </span>
                </div>

                {/* ---------- CONTENT ---------- */}
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={headerId}
                  className="accordion-content-wrapper"
                  style={{
                    maxHeight: isExpanded ? '1000px' : '0px',
                  }}
                >
                  <div className="accordion-content">
                    <div className="card-content">
                      <img
                        src={
                          item?.fields?.CardMedia?.value?.src ||
                          item?.fields?.CardMediaMobile?.value?.src
                        }
                        alt={item?.fields?.CardTitle?.value}
                        className="card-image"
                        loading="lazy"
                      />

                      <div className="card-heading">
                        <span className="main-header-text roboto-bold">
                          {item?.fields?.CardTitle?.value}
                        </span>
                      </div>

                      <p className="card-snippet roboto-regular">
                        {item?.fields?.CardSubtitle?.value}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {slides.length > 3 && (
            <div className="show-more-less-btns">
              {!showAll ? (
                <button className="show-more show-btns" onClick={() => setShowAll(true)}>
                  <span>Show More</span>
                </button>
              ) : (
                <button
                  className="show-less show-btns"
                  onClick={() => {
                    setShowAll(false);
                    setExpandedIndex(null);
                  }}
                >
                  <span>Show Less</span>
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        /* ================= DESKTOP SLIDER ================= */
        <div className="always-connected">
          <Slider {...settings}>
            {slides.map((slide, index) => (
              <div className="always-connected-slider-card" key={index}>
                <div className="always-connected-slider-image">
                  <img
                    src={slide?.fields?.CardMedia?.value?.src}
                    alt={slide?.fields?.CardTitle?.value}
                    loading="lazy"
                  />
                </div>

                <div className="always-connected-slider-content">
                  <h2 className="roboto-bold always-connected-title">
                    {slide?.fields?.CardTitle?.value}
                  </h2>
                  <p className="roboto-regular always-connected-subtitle">
                    {slide?.fields?.CardSubtitle?.value}
                  </p>
                </div>
              </div>
            ))}
          </Slider>
        </div>
      )}
    </div>
  );
};

export default React.memo(AlwaysConnectedSlider);
